import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL, V3_APP_BASE_URL } from '@/constants/Config';
import { normalizeStoreApiResponse } from '@/utilities/StoreV4ApiUtils';
import type {
  ICreateStoreRequest,
  ICreateStoreResponse,
  IUpdateStoreRequest,
  IUpdateStoreDonationMarkupRequest,
  ICreateV4OrderRequest,
  ICreateV4OrderResponse,
  ILaunchStoreRequest,
  IStoreApiResponse,
  IStoreResponse,
} from '@/common/interfaces/store.interface';
import type {
  IStoreInviteMember,
  IStoreInviteMembersQuery,
  IStoreInviteMembersResponse,
  IStoreInviteReminderSendPayload,
  IStoreInviteRemindersPayload,
  IStoreInviteSendPayload,
  IStoreInviteStatus,
} from '@/common/interfaces/store-invites.interface';
import { mergeStoreInviteMemberPages } from '@/modules/Orders/OrderDetail/StoreInvites/helpers/storeInviteMembers.utils';

interface IUpdateStorePayload {
  order_id: number;
  [key: string]: unknown;
}

interface IStoreInviteMembersApiResponse {
  count?: number;
  total?: number;
  members?: IStoreInviteMember[];
  rows?: IStoreInviteMember[];
  occupied_phones?: string[];
  data?: {
    count?: number;
    total?: number;
    members?: IStoreInviteMember[];
    rows?: IStoreInviteMember[];
    occupied_phones?: string[];
  };
}

export const storeApi = createApi({
  reducerPath: 'storeApi',
  tagTypes: ['StoreDetails', 'StoreInviteStatus', 'StoreInviteMembers'],
  baseQuery: axiosBaseQuery({ baseUrl: V3_APP_BASE_URL }),
  endpoints: (build) => ({
    createStore: build.mutation<ICreateStoreResponse, ICreateStoreRequest>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/store/create-store`,
        method: 'post',
        data,
      }),
      invalidatesTags: ['StoreDetails'],
    }),
    updateStore: build.mutation<ICreateStoreResponse, IUpdateStoreRequest>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/store/update-store`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_result, _error, { order_id: orderId, address, ...rest }) => {
        const isAddressOnlyUpdate =
          address != null && Object.keys(rest).filter((key) => key !== 'order_id').length === 0;

        if (isAddressOnlyUpdate) {
          return [];
        }

        return [{ type: 'StoreDetails', id: String(orderId) }];
      },
    }),
    updateStoreDonationMarkup: build.mutation<void, IUpdateStoreDonationMarkupRequest>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/store/donation-markup`,
        method: 'put',
        data,
      }),
    }),
    createV4Order: build.mutation<ICreateV4OrderResponse, ICreateV4OrderRequest>({
      query: (data) => ({
        url: 'api/v4/order/create-order',
        method: 'post',
        data,
      }),
      invalidatesTags: ['StoreDetails'],
    }),
    updateV4Order: build.mutation<ICreateV4OrderResponse, ICreateV4OrderRequest>({
      query: (data) => ({
        url: 'api/v4/order/update-order',
        method: 'put',
        data,
      }),
      invalidatesTags: ['StoreDetails'],
    }),
    getStoreDetails: build.query<IStoreApiResponse, string | number>({
      query: (storeId) => ({
        url: `${API_SERVER_URL}api/store/hq/${storeId}`,
        method: 'get',
      }),
      transformResponse: (response: IStoreApiResponse) =>
        normalizeStoreApiResponse(response) as IStoreApiResponse,
      providesTags: (_result, _error, storeId) => [{ type: 'StoreDetails', id: String(storeId) }],
    }),
    updateV4Store: build.mutation<
      IStoreApiResponse,
      { storeId: string | number; data: IUpdateStorePayload }
    >({
      query: ({ storeId, data }) => ({
        url: `api/v4/store/${storeId}`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    launchStore: build.mutation<
      { status: string; data?: unknown },
      { storeId: string | number; payload: ILaunchStoreRequest }
    >({
      query: ({ storeId, payload }) => ({
        url: `${API_SERVER_URL}api/store/${storeId}/launch`,
        method: 'post',
        data: payload,
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    validateStoreChecks: build.query<{ status: string; data?: unknown }, string | number>({
      query: (storeId) => ({
        url: `api/v4/store/${storeId}/validations`,
        method: 'get',
      }),
    }),
    getOrgCompanyName: build.query<{ data: string }, number>({
      query: (clientId) => ({
        url: `api/v4/users/get-organization-by-client-id`,
        method: 'get',
        params: { clientId },
      }),
    }),
    deleteDesignGroupFromStore: build.mutation<
      void,
      { storeId: string | number; designGroupId: string | number }
    >({
      query: ({ storeId, designGroupId }) => ({
        url: `api/v4/store/${storeId}/design-groups/${designGroupId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    deleteDesignFromStore: build.mutation<
      void,
      { storeId: string | number; designId: string | number }
    >({
      query: ({ storeId, designId }) => ({
        url: `api/v4/store/${storeId}/designs/${designId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    deleteDesignsFromStore: build.mutation<void, { storeId: string | number; designIds: number[] }>(
      {
        query: ({ storeId, designIds }) => ({
          url: `api/v4/store/${storeId}/designs`,
          method: 'delete',
          data: { design_ids: designIds },
        }),
        invalidatesTags: (_result, _error, { storeId }) => [
          { type: 'StoreDetails', id: String(storeId) },
        ],
      }
    ),
    deleteStoreFlags: build.mutation<void, { storeId: string | number; flagIds: number[] }>({
      query: ({ storeId, flagIds }) => ({
        url: `api/v4/store/${storeId}/flags`,
        method: 'delete',
        data: { flag_ids: flagIds },
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    deleteStore: build.mutation<void, string | number>({
      query: (storeId) => ({
        url: `${API_SERVER_URL}api/store/${storeId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, storeId) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    deleteApiServerStoreDesignGroup: build.mutation<
      void,
      { storeId: string | number; designGroupId: string | number }
    >({
      query: ({ storeId, designGroupId }) => ({
        url: `${API_SERVER_URL}api/store/${storeId}/design-groups/${designGroupId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    deleteApiServerStoreDesigns: build.mutation<
      void,
      { storeId: string | number; designIds: number[] }
    >({
      query: ({ storeId, designIds }) => ({
        url: `${API_SERVER_URL}api/store/${storeId}/designs`,
        method: 'delete',
        data: { design_ids: designIds },
      }),
      invalidatesTags: (_result, _error, { storeId }) => [
        { type: 'StoreDetails', id: String(storeId) },
      ],
    }),
    getStoreInviteMembers: build.query<IStoreInviteMembersResponse, IStoreInviteMembersQuery>({
      query: ({ accountId, storeOrderId, group, search, page = 1, limit = 50 }) => ({
        url: `${API_SERVER_URL}api/store/invites/members`,
        method: 'get',
        params: {
          account_id: accountId,
          page,
          limit,
          ...(group ? { group } : {}),
          ...(storeOrderId ? { store_order_id: storeOrderId } : {}),
          ...(search ? { search } : {}),
        },
      }),
      transformResponse: (response: IStoreInviteMembersApiResponse) => {
        const payload = response.data ?? response;
        const members = payload.members ?? payload.rows ?? [];

        return {
          count: payload.count ?? payload.total ?? members.length,
          members,
          occupied_phones: payload.occupied_phones ?? [],
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs.accountId}-${queryArgs.storeOrderId ?? ''}-${
          queryArgs.group
        }-${queryArgs.search ?? ''}`,
      merge: (currentCache, newItems, { arg }) => {
        currentCache.count = newItems.count;
        currentCache.occupied_phones = newItems.occupied_phones ?? [];
        currentCache.members =
          (arg.page ?? 1) <= 1
            ? newItems.members
            : mergeStoreInviteMemberPages(currentCache.members, newItems.members);
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.page !== previousArg?.page ||
          currentArg?.search !== previousArg?.search ||
          currentArg?.group !== previousArg?.group ||
          currentArg?.accountId !== previousArg?.accountId ||
          currentArg?.storeOrderId !== previousArg?.storeOrderId
        );
      },
      providesTags: (_result, _error, args) => [
        { type: 'StoreInviteMembers', id: String(args.storeOrderId ?? args.accountId ?? 'none') },
      ],
    }),
    getStoreInviteStatus: build.query<IStoreInviteStatus, number>({
      query: (storeOrderId) => ({
        url: `${API_SERVER_URL}api/store/invites/status`,
        method: 'get',
        params: { store_order_id: storeOrderId },
      }),
      transformResponse: (response: { data?: IStoreInviteStatus } | IStoreInviteStatus) => {
        if ('invited_count' in response) {
          return response;
        }

        return (
          response.data ?? {
            invited_count: 0,
            invited_people: [],
            auto_reminders_enabled: false,
            reminder_body: null,
            reminder_send_count: 0,
            last_reminder_sent_at: null,
          }
        );
      },
      providesTags: (_result, _error, storeOrderId) => [
        { type: 'StoreInviteStatus', id: String(storeOrderId) },
      ],
    }),
    sendStoreInvites: build.mutation<unknown, IStoreInviteSendPayload>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/store/invites/send`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_result, _error, { store_order_id: storeOrderId }) => [
        { type: 'StoreInviteStatus', id: String(storeOrderId) },
        { type: 'StoreInviteMembers', id: String(storeOrderId) },
      ],
    }),
    updateStoreInviteReminders: build.mutation<unknown, IStoreInviteRemindersPayload>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/store/invites/reminders`,
        method: 'put',
        data,
      }),
      invalidatesTags: (_result, _error, { store_order_id: storeOrderId }) => [
        { type: 'StoreInviteStatus', id: String(storeOrderId) },
      ],
    }),
    sendStoreInviteReminder: build.mutation<unknown, IStoreInviteReminderSendPayload>({
      query: (data) => ({
        url: `${API_SERVER_URL}api/store/invites/reminders/send`,
        method: 'post',
        data,
      }),
      invalidatesTags: (_result, _error, { store_order_id: storeOrderId }) => [
        { type: 'StoreInviteStatus', id: String(storeOrderId) },
      ],
    }),
  }),
});

export const {
  useCreateStoreMutation,
  useUpdateStoreMutation,
  useUpdateStoreDonationMarkupMutation,
  useCreateV4OrderMutation,
  useUpdateV4OrderMutation,
  useGetStoreDetailsQuery,
  useLazyGetStoreDetailsQuery,
  useUpdateV4StoreMutation,
  useLaunchStoreMutation,
  useLazyValidateStoreChecksQuery,
  useLazyGetOrgCompanyNameQuery,
  useDeleteDesignGroupFromStoreMutation,
  useDeleteDesignFromStoreMutation,
  useDeleteDesignsFromStoreMutation,
  useDeleteStoreFlagsMutation,
  useDeleteStoreMutation,
  useDeleteApiServerStoreDesignGroupMutation,
  useDeleteApiServerStoreDesignsMutation,
  useGetStoreInviteMembersQuery,
  useGetStoreInviteStatusQuery,
  useSendStoreInvitesMutation,
  useUpdateStoreInviteRemindersMutation,
  useSendStoreInviteReminderMutation,
} = storeApi;

export type { IStoreResponse };
